import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AREA_OF_NEED_SHORT, PROVISION_CATEGORY_LABELS } from '../lib/apdrSchemas';
import { ROLES, useUserRole, hasRole } from '../lib/auth/roles';
import {
  acceptEhcpProvisionProposal,
  dismissEhcpProvisionProposal,
  generateEhcpProvisionProposals,
  getSectionFProvisionStatus,
} from '../lib/functions';
import { Icons } from '../lib/Icons';

function destinationLabel(type) {
  return type === 'intervention' ? 'Pull-out intervention' : 'In-class provision';
}

export default function SectionFProvisionPanel({ studentId, student, onAccepted, filterDestination = 'all', showUnmet = true }) {
  const { role } = useUserRole();
  const canEdit = hasRole(role, [ROLES.SENCO]);
  const hasEhcp = !!student?.hasEHCP;

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [unmetEntitlements, setUnmetEntitlements] = useState([]);
  const [summary, setSummary] = useState({});
  const [busyId, setBusyId] = useState(null);
  const autoImportAttempted = useRef(false);

  const hasSectionFData = Boolean(
    student?.ehcpDetails?.sectionF?.v2Categories
    || (Array.isArray(student?.ehcpDetails?.sectionF?.provision) && student.ehcpDetails.sectionF.provision.length)
    || student?.ehcpDetails?.sectionF?.provisionByArea
  );

  const loadStatus = useCallback(async () => {
    if (!studentId || !hasEhcp) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await getSectionFProvisionStatus({ studentId });
      const data = result.data || {};
      setProposals(Array.isArray(data.proposals) ? data.proposals : []);
      setUnmetEntitlements(Array.isArray(data.unmetEntitlements) ? data.unmetEntitlements : []);
      setSummary(data.summary || data.fulfillment || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [studentId, hasEhcp]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleGenerate = useCallback(async (force = false) => {
    setGenerating(true);
    try {
      const result = await generateEhcpProvisionProposals({ studentId, force });
      const data = result.data || {};
      if (data.proposalsPending) {
        toast.success(`${data.proposalsPending} proposal(s) ready to review`);
      } else if (force) {
        toast.success('Section F reviewed: no new proposals');
      }
      await loadStatus();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not generate proposals from Section F');
    } finally {
      setGenerating(false);
    }
  }, [studentId, loadStatus]);

  useEffect(() => {
    if (
      !canEdit
      || !hasEhcp
      || loading
      || generating
      || autoImportAttempted.current
      || !hasSectionFData
    ) return;
    if (proposals.length === 0 && unmetEntitlements.length === 0) {
      autoImportAttempted.current = true;
      handleGenerate(false);
    }
  }, [canEdit, hasEhcp, loading, generating, hasSectionFData, proposals.length, unmetEntitlements.length, handleGenerate]);

  const handleAccept = async (proposal, preferWaitlist = false) => {
    setBusyId(proposal.id);
    try {
      const result = await acceptEhcpProvisionProposal({
        proposalId: proposal.id,
        preferWaitlist,
      });
      const data = result.data || {};
      if (data.action === 'waitlisted') {
        toast.success('Added to intervention waitlist: group is full or no slot available');
      } else if (data.action === 'intervention_assigned') {
        toast.success(`Assigned to ${data.interventionName || 'intervention group'}`);
      } else {
        toast.success('Provision added to student record');
      }
      await loadStatus();
      if (onAccepted) onAccepted();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not accept proposal');
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (proposalId, waive = false) => {
    setBusyId(proposalId);
    try {
      await dismissEhcpProvisionProposal({ proposalId, waiveEntitlement: waive });
      toast.success(waive ? 'Dismissed and marked as not required' : 'Proposal dismissed');
      await loadStatus();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not dismiss proposal');
    } finally {
      setBusyId(null);
    }
  };

  if (!hasEhcp) return null;

  const filteredProposals = filterDestination === 'all'
    ? proposals
    : proposals.filter((p) => p.destinationType === filterDestination);

  const filteredUnmet = filterDestination === 'all'
    ? unmetEntitlements
    : unmetEntitlements.filter((e) => e.destinationType === filterDestination);

  const unmetCount = filteredUnmet.length;

  return (
    <div className="space-y-4">
      {showUnmet && unmetCount > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-900 dark:text-amber-200">
              <Icons.ExclamationTriangle className="w-4 h-4" />
              Section F provision not yet met ({unmetCount})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-amber-800 dark:text-amber-300">
              These EHCP entitlements are not yet recorded as active provision or intervention. Review and accept suggestions below, or add provision manually.
            </p>
            <ul className="space-y-2">
              {filteredUnmet.map((ent) => (
                <li
                  key={ent.id}
                  className="rounded border border-amber-200 bg-white/80 px-3 py-2 text-sm dark:border-amber-800 dark:bg-gray-900/50"
                >
                  <span className="font-medium">{ent.sourceText}</span>
                  <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">
                    {AREA_OF_NEED_SHORT[ent.areaOfNeed] || ent.areaOfNeed}
                    {' • '}
                    {destinationLabel(ent.destinationType)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
            {filterDestination === 'intervention'
              ? 'Intervention recommendations from Section F'
              : 'Suggestions from EHCP Section F'}
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {filterDestination === 'intervention'
              ? 'Pull-out therapies and specialist programmes may join a group or the waitlist if full.'
              : 'Review before adding: in-class items go to Provision; pull-out therapies may join a group or the waitlist.'}
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => handleGenerate(filteredProposals.length === 0)}
            disabled={generating}
            className="rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 dark:bg-gray-900 dark:border-indigo-800 dark:text-indigo-300"
          >
            {generating ? 'Analysing Section F...' : filteredProposals.length ? 'Refresh from Section F' : 'Import from Section F'}
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Checking Section F entitlements...</p>
      ) : filteredProposals.length === 0 ? (
        <p className="text-sm text-gray-500">
          No pending Section F suggestions. Upload or re-process the EHCP, or click Import from Section F.
        </p>
      ) : (
        <div className="space-y-3">
          {filteredProposals.map((proposal) => (
            <div
              key={proposal.id}
              className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3 dark:border-indigo-900 dark:bg-indigo-950/20"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{proposal.suggestedName}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                    EHCP: {proposal.sourceText}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                      {destinationLabel(proposal.destinationType)}
                    </span>
                    <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                      {AREA_OF_NEED_SHORT[proposal.areaOfNeed] || proposal.areaOfNeed}
                    </span>
                    {proposal.category && (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                        {PROVISION_CATEGORY_LABELS[proposal.category] || proposal.category}
                      </span>
                    )}
                    {proposal.interventionType && (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200">
                        {proposal.interventionType}
                      </span>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      type="button"
                      disabled={busyId === proposal.id}
                      onClick={() => handleAccept(proposal, false)}
                      className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Accept
                    </button>
                    {proposal.destinationType === 'intervention' && (
                      <button
                        type="button"
                        disabled={busyId === proposal.id}
                        onClick={() => handleAccept(proposal, true)}
                        className="rounded border border-purple-300 px-3 py-1.5 text-xs text-purple-800 hover:bg-purple-50 disabled:opacity-50 dark:text-purple-200"
                      >
                        Waitlist instead
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busyId === proposal.id}
                      onClick={() => handleDismiss(proposal.id, false)}
                      className="rounded px-3 py-1.5 text-xs text-gray-600 hover:underline disabled:opacity-50"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
